// WHAT: Modal component for creating new workspaces
// WHY:  Encapsulates workspace creation UI in reusable component
// DOMAIN: Settings → TimeTracQ → Workspace Management
// DEPENDENCIES: UI components (Modal, Input, Label, Button, Stack)
// RISKS:
//   - WARN: No validation of workspace name format or uniqueness

import { Modal, Input, Label, Button, Stack } from "@/components/ui";

/**
 * WHAT:
 *   Modal component for adding a new workspace.
 *
 * WHY:
 *   Provides reusable UI for workspace creation with form validation.
 *
 * PROPS:
 *   - isOpen: Whether the modal is open
 *   - onClose: Callback to close the modal
 *   - workspaceName: Current workspace name input value
 *   - onWorkspaceNameChange: Callback when workspace name changes
 *   - onAdd: Callback to create the workspace
 *   - saving: Whether the save operation is in progress
 *
 * BEHAVIOR:
 *   - Validates that workspace name is not empty
 *   - Disables submit button when saving or name is empty
 */
interface AddWorkspaceModalProperties {
  isOpen: boolean;
  onAdd: () => void;
  onClose: () => void;
  onWorkspaceNameChange: (name: string) => void;
  saving: boolean;
  workspaceName: string;
}

/**
 * WHAT:
 *   React component that renders a modal for adding a new TimeTracQ workspace.
 *
 * WHY:
 *   Encapsulates the UI and interaction logic for the "Add Workspace" functionality.
 *
 * @param root0
 *
 * @param root0.isOpen
 *
 * @param root0.onAdd
 *
 * @param root0.onClose
 *
 * @param root0.onWorkspaceNameChange
 *
 * @param root0.saving
 *
 * @param root0.workspaceName
 *
 * @returns {JSX.Element} Modal component for adding a new workspace
 *
 */
export function AddWorkspaceModal({
  isOpen,
  onAdd,
  onClose,
  onWorkspaceNameChange,
  saving,
  workspaceName,
}: Readonly<AddWorkspaceModalProperties>) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Workspace">
      <Stack direction="col" gap={4}>
        <Stack direction="col" gap={2}>
          <Label>Workspace Name</Label>
          <Input
            onChange={(e) => {
              onWorkspaceNameChange(e.target.value);
            }}
            placeholder="Enter workspace name..."
            size="md"
            type="text"
            value={workspaceName}
          />
        </Stack>

        <div className="flex justify-end space-x-3">
          <Button onClick={onClose} size="md" variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={saving || !workspaceName.trim()}
            onClick={onAdd}
            size="md"
            variant="primary"
          >
            {saving ? "Creating..." : "Create Workspace"}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}
